// =============================================================================
// API-Only Data Service
// =============================================================================
// Pure API-based data service - NO LOCAL STORAGE USAGE
// All data comes from and goes to the backend API only
// =============================================================================

import { apiService } from './apiService';
import { storage } from '../utils/storage';

// =============================================================================
// API-Only Data Service Functions
// =============================================================================

export const dataService = {

  /**
   * Initializes the API-only data service
   * @param {string} moduleId - Module ID from URL parameter
   * @param {string} authCode - Required auth code from URL parameter
   * @param {string} locale - Current language locale (en, ar, ur, ma)
   */
  initialize: async (moduleId, authCode = null, locale = 'en') => {
    try {
      let accessToken = null;
      
      // Auth code is now mandatory
      if (!authCode) {
        // Return error with code for CMS lookup in App.js
        return { 
          success: false, 
          error: 'Authentication code is required',
          code: 'elearning/app/callback/authmissing' 
        };
      }
      
      // Exchange auth code for access token
      // IMPORTANT: When a new auth code is provided, it means a new user is logging in
      // Clear old progress data to prevent cross-user contamination in localStorage
      // The GET API will fetch fresh data for the new user
      storage.clearProgressData();
      
      const tokenResponse = await apiService.exchangeAuthCode(authCode);
      
      if (tokenResponse.success && tokenResponse.accessToken) {
        accessToken = tokenResponse.accessToken;
        
        // Calculate expiry from expiresIn (seconds) to hours
        const expiryHours = tokenResponse.expiresIn / 3600;
        
        // Store token in localStorage for session persistence
        storage.setAuthToken(accessToken, expiryHours);
        
        return { success: true, accessToken };
      } else {
        // Return error with code for CMS lookup
        return { 
          success: false, 
          error: 'Auth code exchange failed - invalid response from server',
          code: tokenResponse.code // Preserve backend error code if available
        };
      }
    } catch (error) {
      console.error('DataService: Initialize failed:', error);
      // Return error with code for CMS lookup instead of throwing
      return { 
        success: false, 
        error: error.message,
        code: error.code // Preserve backend error code
      };
    }
  },

  /**
   * Gets module content and ALL user data from backend API only
   * NO localStorage usage - everything from API
   * @param {string} moduleId - Module ID from URL parameter
   * @param {string} customToken - Optional custom token to use instead of localStorage
   * @returns {Promise<Object>} Complete module data from backend
   */
  loadModuleData: async (moduleId, customToken = null) => {
    try {
      // GET API - Load everything from backend
      const backendData = await apiService.getModuleContent(moduleId, customToken);
      
      if (backendData && backendData.success) {
        
        // Backend response structure from Postman:
        // {
        //   "success": true,
        //   "elearning": { "slides": [...] },
        //   "startSlide": 1,
        //   "language": "en",
        //   "moduleId": "14" // Dynamic module ID from API response
        // }
        
        // Handle different possible data structures
        let slidesData = null;
        
        // Try different possible locations for slides data
        if (backendData.elearning && backendData.elearning.slides) {
          // Case 1: elearning.slides (most common)
          slidesData = {
            Slides: backendData.elearning.slides
          };
        } else if (backendData.elearning && Array.isArray(backendData.elearning)) {
          // Case 2: elearning is directly an array
          slidesData = {
            Slides: backendData.elearning
          };
        } else if (backendData.slides) {
          // Case 3: slides at root level
          slidesData = {
            Slides: backendData.slides
          };
        } else if (backendData.elearning) {
          // Case 4: elearning exists but no slides property, use as-is
          slidesData = backendData.elearning;
        } else {
          console.error('DataService: No slides data found in API response');
        }
        
        // Normalize slide property names to match component expectations
        if (slidesData && slidesData.Slides) {
          slidesData.Slides = slidesData.Slides.map(slide => ({
            ...slide,
            // Normalize property names to match component expectations
            SlideNumber: slide.SlideNumber || slide.slideNumber,
            Images: slide.Images || slide.images,
            AudioFiles: slide.AudioFiles || slide.audioFiles,
            Videos: slide.Videos || slide.videos,
            Text: slide.Text || slide.text,
            IsQuiz: slide.IsQuiz || slide.isQuiz,
            VideoMargins: slide.VideoMargins || slide.videoMargins,
            CorrectAnswers: slide.CorrectAnswers || slide.correctAnswers,
            // Keep original properties as well for backward compatibility
            slideNumber: slide.slideNumber || slide.SlideNumber,
            images: slide.images || slide.Images,
            audioFiles: slide.audioFiles || slide.AudioFiles,
            videos: slide.videos || slide.Videos,
            text: slide.text || slide.Text,
            isQuiz: slide.isQuiz || slide.IsQuiz,
            videoMargins: slide.videoMargins || slide.VideoMargins,
            correctAnswers: slide.correctAnswers || slide.CorrectAnswers
          }));
        }
        
        // Extract completion data from API response
        const completedSlides = {};
        const completedQuizzes = {};
        
        // Process completedSlides array from API response (NEW FIELD)
        if (backendData.completedSlides && Array.isArray(backendData.completedSlides)) {
          backendData.completedSlides.forEach(slideNumber => {
            completedSlides[slideNumber.toString()] = true;
          });
        }
        
        // Process allQuizStatuses object from API response (NEW FIELD)
        if (backendData.allQuizStatuses && typeof backendData.allQuizStatuses === 'object') {
          Object.keys(backendData.allQuizStatuses).forEach(slideNumber => {
            const quizData = backendData.allQuizStatuses[slideNumber];
            if (quizData && typeof quizData === 'object') {
              completedQuizzes[slideNumber] = {
                attempts: quizData.attempts || 0,
                status: quizData.status || 'pending',
                completed: quizData.completed || false,
                passed: quizData.passed || false,
                // Include any additional fields from API
                ...quizData
              };
            }
          });
        }
        
        // Process user progress data if available (fallback)
        if (backendData.userProgress) {
          // Audio completion status
          if (backendData.userProgress.audioCompletionStatus) {
            Object.keys(backendData.userProgress.audioCompletionStatus).forEach(slideNumber => {
              if (backendData.userProgress.audioCompletionStatus[slideNumber] === true) {
                completedSlides[slideNumber] = true;
              }
            });
          }
          
          // Quiz completion status
          if (backendData.userProgress.quizCompletionStatus) {
            Object.keys(backendData.userProgress.quizCompletionStatus).forEach(slideNumber => {
              const quizData = backendData.userProgress.quizCompletionStatus[slideNumber];
              if (quizData && quizData.completed) {
                completedQuizzes[slideNumber] = quizData;
              }
            });
          }
        }
        
        // If no specific progress data, infer from startSlide
        // All slides before startSlide are considered completed
        const startSlideNumber = backendData.startSlide || 1;
        for (let i = 1; i < startSlideNumber; i++) {
          completedSlides[i.toString()] = true;
        }
        
        // Process isallSlidesCompleted from API response
        // Store it in localStorage like we do for other values (completedSlides, allQuizStatuses, language)
        // IMPORTANT: On GET API (initialization), only update if the API explicitly provides the value
        // 1. If API provides the value, use it (user-specific based on auth token)
        // 2. If API doesn't provide it (undefined), remove it from localStorage (don't keep stale data)
        // 3. This prevents showing false when backend hasn't implemented the field yet
        if (backendData.hasOwnProperty('isallSlidesCompleted')) {
          // API explicitly provided the value, use it
          const apiIsAllSlidesCompleted = backendData.isallSlidesCompleted === true;
          storage.setIsAllSlidesCompleted(apiIsAllSlidesCompleted);
        } else {
          // API didn't provide the value (backend not implemented yet)
          // Remove it from localStorage to avoid showing stale/incorrect data
          // It will be set to true when user actually completes all slides
          try {
            // Use storage utility to remove the key
            localStorage.removeItem('isallSlidesCompleted');
          } catch (error) {
            console.warn('DataService: Error removing isallSlidesCompleted from localStorage:', error);
          }
        }
        
        return {
          success: true,
          lessonData: slidesData,
          currentSlide: (backendData.startSlide || 1) - 1, // Convert to 0-based for UI
          currentLanguage: backendData.language || 'en',
          moduleId: backendData.moduleId || moduleId, // Use dynamic ID from API response
          userProgress: {
            startSlide: backendData.startSlide || 1,
            language: backendData.language || 'en',
            lastCompletedSlide: (backendData.startSlide || 1) - 1,
            completedSlides: completedSlides, // Enhanced with new API field
            completedQuizzes: completedQuizzes, // Enhanced with new API field
            // Include raw API data for reference
            apiCompletedSlides: backendData.completedSlides || [],
            apiAllQuizStatuses: backendData.allQuizStatuses || {},
            isallSlidesCompleted: storage.getIsAllSlidesCompleted()
          }
        };
      } else {
        // Create error with code for CMS lookup
        const errorWithCode = new Error('Backend API returned unsuccessful response');
        errorWithCode.code = backendData.code; // Preserve backend error code if available
        throw errorWithCode;
      }
      
    } catch (error) {
      // No fallback - preserve error code for CMS lookup
      const errorWithCode = new Error(`Failed to load module data: ${error.message}`);
      errorWithCode.code = error.code; // Preserve backend error code
      throw errorWithCode;
    }
  },

  /**
   * Saves user progress to backend API only
   * NO localStorage usage - direct to API
   * @param {Object} progressData - Progress data to save
   * @param {string} progressData.moduleId - Module ID from URL parameter
   * @param {number} progressData.slideNumber - Current slide number (0-based for UI)
   * @param {string} progressData.language - Current language
   * @param {Object} progressData.quizState - Quiz state data (if on quiz slide)
   * @param {Object} progressData.allQuizStatuses - All previous quiz statuses
   * @param {Object} progressData.exitEvent - Exit event data (if user is exiting)
   * @param {string} customToken - Optional custom token to use instead of localStorage
   * @returns {Promise<void>} Promise that resolves when progress is saved to backend
   */
  saveProgressToAPI: async (progressData, customToken = null) => {
    const { moduleId, slideNumber, language, quizState, allQuizStatuses, completedSlides, exitEvent, isallSlidesCompleted } = progressData;
    
    try {
      // Determine the final isallSlidesCompleted value
      // Priority: 1) Explicitly passed value, 2) Current storage value (if already true)
      // If explicitly passed as true, use it. Otherwise, preserve existing true value from storage
      const currentIsAllSlidesCompleted = storage.getIsAllSlidesCompleted();
      
      let finalIsAllSlidesCompleted;
      if (isallSlidesCompleted !== undefined && isallSlidesCompleted !== null) {
        // Explicitly passed value takes priority
        finalIsAllSlidesCompleted = isallSlidesCompleted === true;
      } else {
        // No explicit value passed, preserve existing storage value (if true)
        finalIsAllSlidesCompleted = currentIsAllSlidesCompleted;
      }
      
      // Update storage if it's being set to true
      if (finalIsAllSlidesCompleted) {
        storage.setIsAllSlidesCompleted(true);
      }
      
      // Prepare standardized API payload
      const apiPayload = {
        moduleId: moduleId,
        slideNumber: slideNumber, // Already 1-based from App.js
        language: language,
        allQuizStatuses: allQuizStatuses || {}, // Format: {5: {attempts: 2, status: "passed"}}
        completedSlides: completedSlides || [], // Format: [1, 2, 3, 4, 5]
        isallSlidesCompleted: finalIsAllSlidesCompleted
      };
      
      // Add optional fields
      if (quizState) {
        apiPayload.quizState = quizState;
      }
      if (exitEvent) {
        apiPayload.exitEvent = exitEvent;
      }
      
      // POST API - Save to backend with standardized format
      await apiService.updateProgress(apiPayload, customToken);
      
    } catch (error) {
      throw error; // Re-throw so calling code can handle
    }
  },

  /**
   * Helper function to save auth token (only localStorage usage)
   * @param {string} authToken - JWT authentication token
   * @param {number} expiryInHours - Token expiry time in hours (default: 24)
   */
  saveAuthToken: (authToken, expiryInHours = 24) => {
    try {
      storage.setAuthToken(authToken, expiryInHours);
    } catch (error) {
      // Silent error handling
    }
  },

  /**
   * Clear auth token from localStorage (for logout)
   */
  clearAuthToken: () => {
    try {
      storage.clearAuthToken();
    } catch (error) {
      // Silent error handling
    }
  },

  /**
   * Check if we have a valid auth token
   * @returns {boolean} True if valid token exists
   */
  hasValidToken: () => {
    return storage.hasValidToken();
  }

};

// =============================================================================
// Export for external access
// =============================================================================
export default dataService;